<template>
  <HotelOffersListModal :isOpen="isHotelOffersModalOpen" @closeModal="hideOffers" />
  <div>
    <div class="tw:flex tw:flex-col tw:justify-center tw:items-center tw:w-full tw:mb-12 tw:text-center">
      <h3 class="tw:text-2xl tw:font-bold tw:mb-2">{{ $t('studio_plus_mp_subheader') }}</h3>
      <p class="tw:text-xl tw:text-gray-700">{{ $t('plus_product_page_features_subtext') }}</p>
    </div>
    <div
      class="tw:flex tw:gap-4 tw:flex-col tw:md:flex-row"
      :class="activeTab !== null ? 'tw:items-start' : 'tw:items-center'"
    >
      <div class="tw:w-full tw:md:w-1/2">
        <div
          v-for="item in products"
          :key="item.id"
          :class="getActiveClass(item)"
          class="tw:mb-2 tw:p-4 tw:flex tw:flex-col tw:rounded-xl tw:gap-x-4 tw:w-full"
        >
          <StudioPlusProduct :item="item" :activeTab="activeTab" @openTab="openTab" @showOffers="showOffers" />
        </div>
      </div>
      <div class="tw:hidden tw:md:flex tw:md:w-1/2">
        <Transition name="fade" mode="out-in">
          <img v-if="activeTab === null" :src="CoverImage" alt="Studio Plus Smart Features" />
          <Videoplayer
            v-else
            :src="videoUrl"
            :autoplay="true"
            height="400px"
            width="100%"
            :muted="true"
            :controls="false"
            :id="activeTab"
            :key="activeTab"
            @clickEventTriggered="clickEventTriggered"
          />
        </Transition>
      </div>
    </div>
  </div>
</template>
<script setup>
import { computed, ref } from 'vue'
import Videoplayer from '@/components/studioPlus/Videoplayer.vue'
import HotelOffersListModal from '@/components/hotelOffers/HotelOffersListModal.vue'
import StudioPlusProduct from '@/components/studioPlus/StudioPlusProduct.vue'

import appAnalytics from '@/utils/tracking'

import { selectedAccommodationId } from '@/layouts/queries'
import { segmentEventsNamesVideo, segmentEventsNamesTile } from '@/constants/studioPlusProductEvents.ts'

const HotelConnect = new URL('../../assets/img/studio-plus/icons/HotelConnect.svg', import.meta.url)
const RateInsights = new URL('../../assets/img/studio-plus/icons/RateInsights.svg', import.meta.url)
const TravelerTrends = new URL('../../assets/img/studio-plus/icons/TravelerTrends.svg', import.meta.url)
const HotelOffers = new URL('../../assets/img/studio-plus/icons/HotelOffers.svg', import.meta.url)
const MoreFeatures = new URL('../../assets/img/studio-plus/icons/MoreFeatures.svg', import.meta.url)
const CoverImage = new URL(`../../assets/img/studio-plus/plus-smart-features-cover.svg`, import.meta.url)
const HotelConnectVideo = new URL('../../assets/videos/HotelConnect.mp4', import.meta.url)
const RateInsightsVideo = new URL('../../assets/videos/RateInsights.mp4', import.meta.url)
const TravelerTrendsVideo = new URL('../../assets/videos/VisitorsProfile.mp4', import.meta.url)
const HotelOffersVideo = new URL('../../assets/videos/HotelOffers.mp4', import.meta.url)

const isHotelOffersModalOpen = ref(false)
const activeTab = ref(null)

const products = [
  {
    title: 'hotel_connect',
    id: 0,
    subtitle: 'plus_product_page_hc_subtext',
    icon: HotelConnect,
    isDisabled: false,
    list: ['plus_product_page_hc_point_1', 'plus_product_page_hc_point_2', 'plus_product_page_hc_point_3'],
  },
  {
    title: 'app.rateInsights.name',
    id: 1,
    subtitle: 'plus_product_page_ri_subtext',
    icon: RateInsights,
    isDisabled: false,
    list: ['plus_product_page_ri_point_1', 'plus_product_page_ri_point_2', 'plus_product_page_ri_point_3'],
  },
  {
    title: 'app.visitorsProfile.name',
    id: 2,
    subtitle: 'plus_product_page_vp_subtext',
    icon: TravelerTrends,
    isDisabled: false,
    list: ['plus_product_page_vp_point_1', 'plus_product_page_vp_point_2', 'plus_product_page_vp_point_3'],
  },
  {
    title: 'hotel_offers_product_header',
    id: 3,
    subtitle: 'business_studio_plus_benefit_1_subtext',
    icon: HotelOffers,
    isDisabled: false,
    list: ['plus_product_page_ho_point_1', 'plus_product_page_ho_point_2', 'plus_product_page_ho_point_3'],
  },
  {
    title: 'plus_product_page_new_feature',
    id: 4,
    subtitle: '',
    icon: MoreFeatures,
    isDisabled: true,
  },
]

const videoUrl = computed(() => {
  if (activeTab.value === 0) return HotelConnectVideo
  if (activeTab.value === 1) return RateInsightsVideo
  if (activeTab.value === 2) return TravelerTrendsVideo
  if (activeTab.value === 3) return HotelOffersVideo
  else return null
})

const clickEventTriggered = id => {
  appAnalytics.track(segmentEventsNamesVideo[id], {
    accommodationId: selectedAccommodationId.value,
    source: 'Business Studio Plus Page',
  })
}
const showOffers = () => {
  appAnalytics.track(appAnalytics.events.SP_SHOW_OFFERS, {
    accommodationId: selectedAccommodationId.value,
    source: 'Business Studio Plus Page',
  })
  isHotelOffersModalOpen.value = true
}
const hideOffers = () => {
  isHotelOffersModalOpen.value = false
}

const openTab = (event, id) => {
  if (event.target?.id === 'show-offers-btn') return

  if (id !== 4 && id !== activeTab.value) {
    appAnalytics.track(segmentEventsNamesTile[id], {
      accommodationId: selectedAccommodationId.value,
      source: 'Business Studio Plus Page',
    })
  }
  activeTab.value = id === 4 || id === activeTab.value ? null : id
}

const getActiveClass = item => {
  const classes = []
  if (!item.isDisabled) {
    classes.push('tw:hover:cursor-pointer')
  }
  if (activeTab.value === item.id) {
    if (activeTab.value === 0) {
      classes.push('tw:bg-orange-100')
    } else if (activeTab.value === 1) {
      classes.push('tw:bg-red-100')
    } else if (activeTab.value === 2) {
      classes.push('tw:bg-blue-100')
    } else if (activeTab.value === 3) {
      classes.push('tw:bg-green-100')
    }
  }
  return classes.join(' ')
}
</script>
<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: all 0.3s ease;
}

.fade-enter-from {
  opacity: 0;
  transform: translateX(20px);
}

.fade-leave-to {
  opacity: 0;
  transform: translateX(-20px);
}
</style>
